import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import { revalidatePublicPath } from '../../utils/revalidatePublic';

// Tailwind-only spinner (border class passed so Tailwind can include it)
const TailwindSpinner = ({ size = 'h-5 w-5', borderClass = 'border-blue-500' }) => (
  <div
    role="status"
    aria-hidden="false"
    className={`animate-spin rounded-full ${size} border-t-2 border-b-2 ${borderClass}`}
  />
);

const BorderStateAdmin = () => {
  const [states, setStates] = useState([]);
  const [, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [loadingPublish, setLoadingPublish] = useState(false);

  const toast = useRef(null);

  const showMessage = useCallback((type, text) => {
    toast.current?.show({
      severity: type === 'success' ? 'success' : 'error',
      summary: type === 'success' ? 'Success' : 'Error',
      detail: text,
      life: 5000,
    });
  }, []);

  const fetchStates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/border_states');
      setStates(response.data);
      showMessage('success', 'Border states loaded successfully.');
    } catch (error) {
      showMessage('error', 'Error fetching border states. Check console for details.');
      console.error('Fetch Error:', error.response ? error.response.data : error.message);
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

const refreshStates = () => {
    setLoading(true);
    const paths = ['/es', '/en', '/']; // Incluí '/' ya que mencionas "home pages"
    setLoadingPublish(true);
    (async () => {
        try {
            const url = process.env.REACT_APP_BORDERGARITA_PUBLIC_APP_URL
            const secret = process.env.REACT_APP_BORDERGARITA_REVALIDATE_SECRET

            // 1. Crear un array de promesas de fetch, una por cada path
            const revalidationPromises = paths.map(async path => {
                try {
                await revalidatePublicPath(url, secret, path);
                console.log(`Revalidation for ${path} ok`);
                return { path, status: 'success' };
              } catch (err) {
                console.error(`Revalidation Error for ${path}:`, err);
                return { path, status: 'error', error: err };
              }
            });

            // 2. Esperar a que todas las promesas se resuelvan
            const results = await Promise.all(revalidationPromises);

            // Opcional: Verificar si hubo errores
            const failedPaths = results.filter(r => r.status === 'error').map(r => r.path);
            
            if (failedPaths.length > 0) {
                // showMessage('error', `Revalidated some paths, but failed for: ${failedPaths.join(', ')}.`);
            } else {
                setLoadingPublish(false);
                // showMessage('success', 'All specified paths revalidated successfully.');
                toast.current.show({ severity: 'success', summary: 'Success', detail: 'Home pages has been published', life: 3000 });
            }

            // 3. Refrescar el estado local después de la revalidación
            // await fetchStates();
            setLoadingPublish(false);

        } catch (error) {
            // Este catch manejará errores de configuración inicial o de fetchStates
            showMessage('error', 'An unexpected error occurred during revalidation or data fetch. Check console for details.');
            console.error('Revalidate/Fetch Error:', error);
        } finally {
            setLoading(false);
            setLoadingPublish(false);
        }
    })();
}

  useEffect(() => {
    fetchStates();
  }, [fetchStates]);

  const handleInputChange = (id, field, value) => {
    setStates(prev =>
      prev.map(s => (s._id === id ? { ...s, [field]: value } : s))
    );
  };

  const handleSave = useCallback(
    async (state) => {
      setSavingId(state._id);
      try {
        const payload = {
          description_es: state.description_es,
          description_en: state.description_en,
        };
        const response = await api.put(`/border_states/${state._id}`, payload);
        const updatedState = response.data;

        setStates(prev => prev.map(s => (s._id === state._id ? updatedState : s)));
        // showMessage('success', `State ${state.name} updated successfully.`);
        // this.toast.current.show({ severity: 'success', summary: 'Success', detail: `Updated ${state.name} successfully`, life: 3000 });
        toast.current.show({ severity: 'success', summary: 'Success', detail: `The descriptions for ${state.name} are saved`, life: 3000 });
      } catch (error) {
        showMessage('error', `Failed to update ${state.name}. Check console for details.`);
        toast.current.show({ severity: 'error', summary: 'Error', detail: `Failed to update ${state.name}`, life: 3000 });
        console.error('Save Error:', error.response ? error.response.data : error.message);
      } finally {
        setSavingId(null);
      }
    },
    [showMessage]
  );

  // if (loading) {
  //   return (
  //     <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
  //       <div className="flex items-center space-x-4">
  //         <TailwindSpinner size="h-10 w-10" borderClass="border-blue-500" />
  //         <p className="text-gray-700 text-lg">Loading border states...</p>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <>
      <Toast ref={toast} />
      <div className="min-h-screen bg-gray-100 py-1 px-4">
        <div className="max-w-7xl mx-auto">
          {/* message UI removed — toast is used instead */}

          <header className="mb-2">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex-1">
                <h3 className="text-3xl font-bold text-gray-800 border-b-4 border-blue-500">
                  Border States Administration
                </h3>
                <p className="mt-2 text-gray-600">
                  Manage and update border state descriptions in multiple languages on Home Pages.
                </p>
              </div>
              <div>
                <Button
                  onClick={refreshStates}
                  type='button'
                  severity="warning"
                  icon="pi pi-plus"
                  loading={loadingPublish}
                  label="Publish home changes"
                  className="mt-2 px-4 py-2 bg-orage text-white rounded-lg hover:bg-orange-700 shadow-md transition"
                >
                </Button>
              </div>
            </div>
          </header>

          <main>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {states.map((state) => (
                <article
                  key={state._id}
                  className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-shadow duration-200"
                  aria-labelledby={`state-${state._id}`}
                >
                  <div className="flex items-center justify-between mb-4 border-b pb-3">
                    <h2 id={`state-${state._id}`} className="text-2xl font-bold text-blue-700">
                      {state.name}
                    </h2>
                    {savingId === state._id && (
                      <div className="flex items-center text-blue-500">
                        <TailwindSpinner size="h-5 w-5" borderClass="border-blue-500" />
                        <span className="ml-2 text-sm font-medium">Saving...</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-semibold text-gray-600 mb-2 block">CODE / NAME</label>
                      <div className="flex gap-2">
                        <InputText
                          type="text"
                          value={state.code}
                          disabled
                          className="w-1/4 p-2 rounded-lg border border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed"
                          aria-label={`Code for ${state.name}`}
                        />
                        <InputText
                          type="text"
                          value={state.name}
                          disabled
                          className="w-3/4 p-2 rounded-lg border border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed"
                          aria-label={`Name for ${state.name}`}
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor={`es-${state._1d}`} className="font-semibold text-gray-800 mb-1 block">
                        Description (ES)
                      </label>
                      <InputTextarea
                        id={`es-${state._id}`}
                        value={state.description_es || ''}
                        onChange={(e) => handleInputChange(state._id, 'description_es', e.target.value)}
                        rows={10}
                        placeholder="Escriba la descripción en español aquí..."
                        className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      />
                    </div>

                    <div>
                      <label htmlFor={`en-${state._id}`} className="font-semibold text-gray-800 mb-1 block">
                        Description (EN)
                      </label>
                      <InputTextarea
                        id={`en-${state._id}`}
                        value={state.description_en || ''}
                        onChange={(e) => handleInputChange(state._id, 'description_en', e.target.value)}
                        rows={10}
                        placeholder="Type the English description here..."
                        className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      />
                    </div>

                   <div>
                      <Button
                        onClick={() => handleSave(state)}
                        disabled={savingId === state._id}
                        aria-busy={savingId === state._id}
                        label="Save Changes"
                        loading={savingId === state._id}
                        // Clases Flex/Centrado aplicadas al contenedor Button
                        className={`w-full mt-2 flex items-center justify-center gap-3 px-4 py-3 rounded-lg font-semibold text-white transition ${savingId === state._id
                          ? 'bg-gray-400 cursor-wait shadow-none'
                          : 'bg-blue-600 hover:bg-blue-700 shadow-md'
                          }`}
                      >
                      </Button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default BorderStateAdmin;